import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ArrowLeft,
  Plus,
  Calendar,
  CheckCircle2,
  Clock,
  MoreVertical,
  Pencil,
  Trash2,
  Settings,
  PlayCircle,
  Package2,
  AlertTriangle,
  Check,
  Loader2,
  X,
  ShoppingCart,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const taskStatusOptions = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

const priorityOptions = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [taskSheetOpen, setTaskSheetOpen] = useState(false);
  const [phaseSheetOpen, setPhaseSheetOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingPhase, setEditingPhase] = useState(null);
  const [taskForm, setTaskForm] = useState({
    project_id: projectId,
    phase_id: "none",
    name: "",
    description: "",
    status: "pending",
    priority: "medium",
    assigned_to: "unassigned",
    start_date: "",
    end_date: "",
    duration_days: 1,
    notes: "",
  });
  const [phaseForm, setPhaseForm] = useState({
    project_id: projectId,
    name: "",
    order: 0,
    status: "pending",
    start_date: "",
    end_date: "",
  });

  // Required Parts state
  const [requiredParts, setRequiredParts] = useState(null);
  const [standardItems, setStandardItems] = useState([]);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [selectedStandardItem, setSelectedStandardItem] = useState("");
  const [itemQuantity, setItemQuantity] = useState("1");
  const [addingItem, setAddingItem] = useState(false);
  const [removingItemId, setRemovingItemId] = useState(null);
  
  // Project Items state (new)
  const [projectItems, setProjectItems] = useState([]);
  const [operations, setOperations] = useState([]); // Used as phases
  const [customers, setCustomers] = useState([]);
  const [updatingPhase, setUpdatingPhase] = useState(false);
  
  // One-off item state
  const [oneoffDialogOpen, setOneoffDialogOpen] = useState(false);
  const [parts, setParts] = useState([]);
  const [oneoffForm, setOneoffForm] = useState({
    name: "",
    description: "",
    bom: [],
    quantity: 1,
    save_as_standard: false,
  });
  const [selectedPart, setSelectedPart] = useState("");
  const [partQuantity, setPartQuantity] = useState("1");
  const [partCost, setPartCost] = useState("");
  
  // BOM Detail Modal state
  const [bomDetailOpen, setBomDetailOpen] = useState(false);
  const [selectedItemForBOM, setSelectedItemForBOM] = useState(null);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        withCredentials: true,
      });
      setProject(response.data);
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team`, {
        withCredentials: true,
      });
      setTeamMembers(response.data);
    } catch (error) {
      console.error("Error fetching team:", error);
    }
  };

  const fetchRequiredParts = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/required-parts`, {
        withCredentials: true,
      });
      setRequiredParts(response.data);
    } catch (error) {
      console.error("Error fetching required parts:", error);
    }
  };

  const fetchStandardItems = async () => {
    try {
      const response = await axios.get(`${API}/standard-items`, {
        withCredentials: true,
      });
      setStandardItems(response.data || []);
    } catch (error) {
      console.error("Error fetching standard items:", error);
    }
  };

  const fetchProjectItems = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/items`, {
        withCredentials: true,
      });
      setProjectItems(response.data || []);
    } catch (error) {
      console.error("Error fetching project items:", error);
    }
  };

  const fetchPhaseOptions = async () => {
    try {
      const response = await axios.get(`${API}/projects/phases/options`, {
        withCredentials: true,
      });
      setOperations(response.data || []);
    } catch (error) {
      console.error("Error fetching phase options:", error);
      // Fallback to default phases if API fails
      setOperations(["Quote", "Planning", "In Progress", "Quality Check", "Complete"]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        withCredentials: true,
      });
      setCustomers(response.data || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchParts = async () => {
    try {
      const response = await axios.get(`${API}/parts`, {
        withCredentials: true,
      });
      setParts(response.data || []);
    } catch (error) {
      console.error("Error fetching parts:", error);
    }
  };

  useEffect(() => {
    fetchProject();
    fetchTeam();
    fetchRequiredParts();
    fetchStandardItems();
    fetchProjectItems();
    fetchPhaseOptions();
    fetchCustomers();
    fetchParts();
  }, [projectId]);

  const handleUpdatePhase = async (phaseName) => {
    setUpdatingPhase(true);
    try {
      await axios.put(
        `${API}/projects/${projectId}/phase`,
        { current_phase: phaseName },
        { withCredentials: true }
      );
      setProject(prev => ({ ...prev, current_phase: phaseName }));
      toast.success(`Phase updated to ${phaseName}`);
    } catch (error) {
      toast.error("Failed to update phase");
    } finally {
      setUpdatingPhase(false);
    }
  };

  // Group BOM items by supplier for display
  const groupBOMBySupplier = (item) => {
    const groups = {};
    const bom = item?.bom || [];
    const labourBom = item?.labour_bom || [];
    
    // Add parts
    bom.forEach(bomItem => {
      const supplier = bomItem.supplier_name || "No Supplier";
      if (!groups[supplier]) {
        groups[supplier] = { parts: [], labour: [] };
      }
      groups[supplier].parts.push(bomItem);
    });
    
    // Add labour
    labourBom.forEach(labourItem => {
      const supplier = labourItem.supplier_name || "No Supplier";
      if (!groups[supplier]) {
        groups[supplier] = { parts: [], labour: [] };
      }
      groups[supplier].labour.push(labourItem);
    });
    
    return groups;
  };

  const openBOMDetail = (item) => {
    setSelectedItemForBOM(item);
    setBomDetailOpen(true);
  };

  const handleAddStandardItem = async () => {
    if (!selectedStandardItem) {
      toast.error("Please select a standard item");
      return;
    }
    
    setAddingItem(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/items`,
        {
          project_id: projectId,
          item_type: "standard",
          standard_item_id: selectedStandardItem,
          quantity: parseFloat(itemQuantity) || 1,
        },
        { withCredentials: true }
      );
      toast.success("Standard item added to project");
      setAddItemDialogOpen(false);
      setSelectedStandardItem("");
      setItemQuantity("1");
      fetchProjectItems();
      fetchRequiredParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add item");
    } finally {
      setAddingItem(false);
    }
  };

  const handleRemoveProjectItem = async (itemId) => {
    setRemovingItemId(itemId);
    try {
      await axios.delete(
        `${API}/projects/${projectId}/items/${itemId}`,
        { withCredentials: true }
      );
      toast.success("Item removed from project");
      fetchProjectItems();
      fetchRequiredParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove item");
    } finally {
      setRemovingItemId(null);
    }
  };

  const handleAddPartToOneoff = () => {
    if (!selectedPart) return;
    
    const part = parts.find(p => p.part_id === selectedPart);
    if (!part) return;
    
    const qty = parseFloat(partQuantity) || 1;
    const cost = partCost ? parseFloat(partCost) : part.unit_cost;
    
    setOneoffForm(prev => ({
      ...prev,
      bom: [
        ...prev.bom,
        {
          part_id: part.part_id,
          part_name: part.name,
          part_sku: part.sku,
          quantity: qty,
          unit_cost: cost,
          line_total: qty * cost,
        }
      ]
    }));
    
    setSelectedPart("");
    setPartQuantity("1");
    setPartCost("");
  };

  const handleRemovePartFromOneoff = (index) => {
    setOneoffForm(prev => ({
      ...prev,
      bom: prev.bom.filter((_, i) => i !== index)
    }));
  };

  const handleAddOneoffItem = async () => {
    if (!oneoffForm.name) {
      toast.error("Please enter item name");
      return;
    }
    if (oneoffForm.bom.length === 0) {
      toast.error("Please add at least one part");
      return;
    }
    
    setAddingItem(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/items`,
        {
          project_id: projectId,
          item_type: "oneoff",
          name: oneoffForm.name,
          description: oneoffForm.description,
          bom: oneoffForm.bom,
          quantity: parseFloat(oneoffForm.quantity) || 1,
          save_as_standard: oneoffForm.save_as_standard,
        },
        { withCredentials: true }
      );
      toast.success(oneoffForm.save_as_standard 
        ? "Item added and saved as standard item" 
        : "One-off item added to project"
      );
      setOneoffDialogOpen(false);
      setOneoffForm({
        name: "",
        description: "",
        bom: [],
        quantity: 1,
        save_as_standard: false,
      });
      fetchProjectItems();
      fetchRequiredParts();
      if (oneoffForm.save_as_standard) {
        fetchStandardItems();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add item");
    } finally {
      setAddingItem(false);
    }
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.task_id}`, taskForm, {
          withCredentials: true,
        });
        toast.success("Task updated");
      } else {
        await axios.post(`${API}/tasks`, taskForm, {
          withCredentials: true,
        });
        toast.success("Task created");
      }
      setTaskSheetOpen(false);
      resetTaskForm();
      fetchProject();
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Failed to save task");
    }
  };

  const handlePhaseSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPhase) {
        await axios.put(`${API}/projects/phases/${editingPhase.phase_id}`, phaseForm, {
          withCredentials: true,
        });
        toast.success("Phase updated");
      } else {
        await axios.post(`${API}/projects/phases`, phaseForm, {
          withCredentials: true,
        });
        toast.success("Phase created");
      }
      setPhaseSheetOpen(false);
      resetPhaseForm();
      fetchProject();
    } catch (error) {
      console.error("Error saving phase:", error);
      toast.error("Failed to save phase");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, {
        withCredentials: true,
      });
      toast.success("Task deleted");
      fetchProject();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  const handleDeletePhase = async (phaseId) => {
    try {
      await axios.delete(`${API}/projects/phases/${phaseId}`, {
        withCredentials: true,
      });
      toast.success("Phase deleted");
      fetchProject();
    } catch (error) {
      console.error("Error deleting phase:", error);
      toast.error("Failed to delete phase");
    }
  };

  const resetTaskForm = () => {
    setEditingTask(null);
    setTaskForm({
      project_id: projectId,
      phase_id: "none",
      name: "",
      description: "",
      status: "pending",
      priority: "medium",
      assigned_to: "unassigned",
      start_date: "",
      end_date: "",
      duration_days: 1,
      notes: "",
    });
  };

  const resetPhaseForm = () => {
    setEditingPhase(null);
    setPhaseForm({
      project_id: projectId,
      name: "",
      order: 0,
      status: "pending",
      start_date: "",
      end_date: "",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
      case "in_progress":
        return "bg-blue-50 text-blue-700 ring-blue-600/20";
      default:
        return "bg-slate-50 text-slate-700 ring-slate-600/20";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-50 text-red-700 ring-red-600/20";
      case "medium":
        return "bg-amber-50 text-amber-700 ring-amber-600/20";
      default:
        return "bg-slate-50 text-slate-700 ring-slate-600/20";
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-64"></div>
          <div className="h-48 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 md:p-8 text-center">
        <p className="text-slate-500">Project not found</p>
        <Link to="/projects" className="text-blue-600 hover:underline mt-2 inline-block">
          Back to Projects
        </Link>
      </div>
    );
  }

  const completedTasks = project.tasks?.filter((t) => t.status === "completed").length || 0;
  const totalTasks = project.tasks?.length || 0;
  const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="p-6 md:p-8" data-testid="project-detail">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/projects"
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Projects
        </Link>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
              {project.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              {project.customer_id && (
                <span className="text-slate-500">
                  Customer: {customers.find(c => c.customer_id === project.customer_id)?.name || "Unknown"}
                </span>
              )}
              {project.description && (
                <span className="text-slate-400">•</span>
              )}
              <span className="text-slate-500">
                {project.description || "No description"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Phase Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Phase:</span>
              <Select 
                value={project.current_phase || "none"} 
                onValueChange={(value) => handleUpdatePhase(value === "none" ? null : value)}
                disabled={updatingPhase}
              >
                <SelectTrigger className="w-[160px] rounded-sm" data-testid="phase-selector">
                  <SelectValue placeholder="Select phase" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Phase</SelectItem>
                  {operations.map((phase) => (
                    <SelectItem key={phase} value={phase}>
                      {phase}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {project.due_date && (
              <span className="text-sm text-slate-500 flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Due: {project.due_date}
              </span>
            )}
            {project.start_date && (
              <span className="text-sm text-slate-500 flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {project.start_date} - {project.end_date || "TBD"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Items Summary Card */}
      <Card className="rounded-sm border-slate-200 mb-8">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Project Items</p>
              <p className="text-2xl font-semibold text-slate-900">{projectItems.length} item{projectItems.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Package2 className="h-4 w-4 text-indigo-500" />
                <span>{projectItems.filter(i => i.item_type === "standard").length} standard</span>
              </div>
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-slate-400" />
                <span>{projectItems.filter(i => i.item_type === "oneoff").length} one-off</span>
              </div>
              <div className="flex items-center gap-2 font-medium">
                <span>Total: R{projectItems.reduce((sum, i) => sum + (i.total_cost || 0), 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="items" className="space-y-6">
        <TabsList className="bg-slate-100 rounded-sm">
          <TabsTrigger value="items" className="rounded-sm" data-testid="items-tab">
            Items ({projectItems.length})
          </TabsTrigger>
          <TabsTrigger value="required-parts" className="rounded-sm" data-testid="required-parts-tab">
            Required Parts ({requiredParts?.required_parts?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="timeline" className="rounded-sm" data-testid="timeline-tab">
            Timeline
          </TabsTrigger>
        </TabsList>

        {/* Items Tab */}
        <TabsContent value="items">
          <Card className="rounded-sm border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg">Project Items</CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => setOneoffDialogOpen(true)}
                  data-testid="add-oneoff-item-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add One-off Item
                </Button>
                <Button
                  size="sm"
                  className="rounded-sm bg-slate-900 hover:bg-slate-800"
                  onClick={() => setAddItemDialogOpen(true)}
                  data-testid="add-standard-item-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Standard Item
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {projectItems.length > 0 ? (
                <div className="space-y-3">
                  {projectItems.map((item) => (
                    <div
                      key={item.item_id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-sm border border-slate-100 cursor-pointer hover:bg-slate-100 hover:border-slate-200 transition-colors"
                      onClick={() => openBOMDetail(item)}
                      data-testid={`project-item-${item.item_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                          item.item_type === "standard" ? "bg-indigo-100" : "bg-amber-100"
                        }`}>
                          {item.item_type === "standard" ? (
                            <Package2 className="h-6 w-6 text-indigo-600" />
                          ) : (
                            <Settings className="h-6 w-6 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">
                              {item.standard_item_name || item.name}
                            </p>
                            <Badge variant="outline" className="rounded-sm text-xs">
                              {item.item_type === "standard" ? "Standard" : "One-off"}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                            {(item.standard_item_sku || item.name) && (
                              <code className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">
                                {item.standard_item_sku || `ONEOFF-${item.item_id.slice(-6).toUpperCase()}`}
                              </code>
                            )}
                            <span>Qty: {item.quantity}</span>
                            <span>•</span>
                            <span>{item.bom?.length || 0} parts</span>
                            {(item.labour_bom?.length || 0) > 0 && (
                              <>
                                <span>•</span>
                                <span>{item.labour_bom.length} labour</span>
                              </>
                            )}
                            {item.bom_version_used && (
                              <>
                                <span>•</span>
                                <span>BOM v{item.bom_version_used}</span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-indigo-500 mt-1">Click to view BOM details</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">
                            R{(item.total_cost || 0).toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500">total cost</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-600"
                          onClick={(e) => { e.stopPropagation(); handleRemoveProjectItem(item.item_id); }}
                          disabled={removingItemId === item.item_id}
                        >
                          {removingItemId === item.item_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Package2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-2">No items added yet</p>
                  <p className="text-sm text-slate-400 mb-6">
                    Add standard items or create one-off custom items for this project
                  </p>
                  <div className="flex justify-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      onClick={() => setOneoffDialogOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add One-off Item
                    </Button>
                    <Button
                      size="sm"
                      className="rounded-sm"
                      onClick={() => setAddItemDialogOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Standard Item
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Required Parts Tab */}
        <TabsContent value="required-parts">
          <div className="space-y-6">
            {/* Required Parts List */}
            {requiredParts?.required_parts?.length > 0 ? (
              <Card className="rounded-sm border-slate-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Required Parts</CardTitle>
                    <div className="flex items-center gap-2">
                      {requiredParts.summary?.parts_need_ordering > 0 && (
                        <Badge className="rounded-sm bg-amber-100 text-amber-700">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {requiredParts.summary.parts_need_ordering} needs ordering
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Part</TableHead>
                        <TableHead className="text-center">Lead Time</TableHead>
                        <TableHead className="text-center">Required</TableHead>
                        <TableHead className="text-center">Available</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requiredParts.required_parts.map((part) => {
                        const isLabour = part.status === "not_applicable";
                        const available = part.inventory_status?.available;
                        
                        return (
                          <TableRow key={part.part_id}>
                            <TableCell className="text-sm text-slate-500">
                              {part.supplier_name || "—"}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{part.part_name}</p>
                                <code className="text-xs text-slate-400">{part.part_sku}</code>
                              </div>
                            </TableCell>
                            <TableCell className="text-center text-sm">
                              {part.lead_time_days ? `${part.lead_time_days}d` : "—"}
                            </TableCell>
                            <TableCell className="text-center font-medium">
                              {part.required_qty}
                            </TableCell>
                            <TableCell className="text-center">
                              {isLabour ? (
                                <span className="text-slate-400">—</span>
                              ) : (
                                <span className={available >= part.required_qty ? "text-emerald-600" : "text-amber-600"}>
                                  {available ?? 0}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              R{part.line_total?.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card className="rounded-sm border-slate-200">
                <CardContent className="py-12 text-center">
                  <Package2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-2">No required parts yet</p>
                  <p className="text-sm text-slate-400">
                    Add items to this project to see the aggregated parts list
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Summary Card */}
            {requiredParts?.summary && Object.keys(requiredParts.summary).length > 0 && (
              <Card className="rounded-sm border-slate-200 bg-slate-50">
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-slate-900">
                        {requiredParts.summary.total_parts_count || 0}
                      </p>
                      <p className="text-sm text-slate-500">Total Parts</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-emerald-600">
                        {requiredParts.summary.parts_in_stock || 0}
                      </p>
                      <p className="text-sm text-slate-500">In Stock</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-amber-600">
                        {requiredParts.summary.parts_need_ordering || 0}
                      </p>
                      <p className="text-sm text-slate-500">Needs Ordering</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-slate-900">
                        R{(requiredParts.summary.total_cost || 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-slate-500">Total Cost</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Removed duplicate content */}

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <Card className="rounded-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Project Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {projectItems.length > 0 ? (
                <div className="space-y-4">
                  {projectItems.map((item, index) => (
                    <div key={item.item_id} className="relative pl-8 pb-6">
                      {index < projectItems.length - 1 && (
                        <div className="absolute left-[11px] top-6 w-0.5 h-full bg-slate-200" />
                      )}
                      <div className="absolute left-0 top-1 w-6 h-6 rounded-full border-2 border-slate-300 bg-white flex items-center justify-center">
                        <Package2 className="h-3 w-3 text-slate-400" />
                      </div>
                      <div className="bg-slate-50 rounded-sm p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-slate-900">
                            {item.standard_item_name || item.name}
                          </p>
                          <Badge variant="outline" className="rounded-sm">
                            {item.item_type === "standard" ? "Standard" : "One-off"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span>Qty: {item.quantity}</span>
                          <span>•</span>
                          <span>{item.bom?.length || 0} parts</span>
                          <span>•</span>
                          <span>R{(item.total_cost || 0).toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          Added: {new Date(item.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500">No items in project yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Task Sheet */}
      <Sheet
        open={taskSheetOpen}
        onOpenChange={(open) => {
          setTaskSheetOpen(open);
          if (!open) resetTaskForm();
        }}
      >
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingTask ? "Edit Task" : "Add Task"}</SheetTitle>
            <SheetDescription>
              {editingTask ? "Update task details" : "Create a new task for this project"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleTaskSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="task-name">Task Name *</Label>
              <Input
                id="task-name"
                value={taskForm.name}
                onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                placeholder="Enter task name"
                required
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Enter description"
                rows={2}
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={taskForm.status}
                  onValueChange={(value) => setTaskForm({ ...taskForm, status: value })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {taskStatusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorityOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Assigned To</Label>
              <Select
                value={taskForm.assigned_to}
                onValueChange={(value) => setTaskForm({ ...taskForm, assigned_to: value })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers.map((member) => (
                    <SelectItem key={member.member_id} value={member.member_id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Phase</Label>
              <Select
                value={taskForm.phase_id}
                onValueChange={(value) => setTaskForm({ ...taskForm, phase_id: value })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select phase" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No phase</SelectItem>
                  {project.phases?.map((phase) => (
                    <SelectItem key={phase.phase_id} value={phase.phase_id}>
                      {phase.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={taskForm.start_date}
                  onChange={(e) => setTaskForm({ ...taskForm, start_date: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={taskForm.end_date}
                  onChange={(e) => setTaskForm({ ...taskForm, end_date: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Duration (days)</Label>
              <Input
                type="number"
                min="1"
                value={taskForm.duration_days}
                onChange={(e) => setTaskForm({ ...taskForm, duration_days: parseInt(e.target.value) || 1 })}
                className="rounded-sm"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setTaskSheetOpen(false);
                  resetTaskForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
              >
                {editingTask ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Phase Sheet */}
      <Sheet
        open={phaseSheetOpen}
        onOpenChange={(open) => {
          setPhaseSheetOpen(open);
          if (!open) resetPhaseForm();
        }}
      >
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{editingPhase ? "Edit Phase" : "Add Phase"}</SheetTitle>
            <SheetDescription>
              {editingPhase ? "Update phase details" : "Create a new phase for this project"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handlePhaseSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phase-name">Phase Name *</Label>
              <Input
                id="phase-name"
                value={phaseForm.name}
                onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })}
                placeholder="Enter phase name"
                required
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Order</Label>
                <Input
                  type="number"
                  min="0"
                  value={phaseForm.order}
                  onChange={(e) => setPhaseForm({ ...phaseForm, order: parseInt(e.target.value) || 0 })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={phaseForm.status}
                  onValueChange={(value) => setPhaseForm({ ...phaseForm, status: value })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {taskStatusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={phaseForm.start_date}
                  onChange={(e) => setPhaseForm({ ...phaseForm, start_date: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={phaseForm.end_date}
                  onChange={(e) => setPhaseForm({ ...phaseForm, end_date: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPhaseSheetOpen(false);
                  resetPhaseForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
              >
                {editingPhase ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Add Standard Item Dialog */}
      <Dialog open={addItemDialogOpen} onOpenChange={setAddItemDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Standard Item to Project</DialogTitle>
            <DialogDescription>
              Select a standard item to add to this project. Its bill of materials will be used to calculate required parts.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Standard Item</Label>
              <Select value={selectedStandardItem} onValueChange={setSelectedStandardItem}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select a standard item" />
                </SelectTrigger>
                <SelectContent>
                  {standardItems.length > 0 ? (
                    standardItems.map((item) => (
                      <SelectItem key={item.item_id} value={item.item_id}>
                        <div className="flex items-center gap-2">
                          <span>{item.name}</span>
                          {item.sku && (
                            <span className="text-xs text-slate-400">({item.sku})</span>
                          )}
                          <span className="text-xs text-slate-400">
                            - {item.bom_count || 0} parts
                          </span>
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>
                      No standard items available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {standardItems.length === 0 && (
                <p className="text-sm text-amber-600">
                  No standard items exist yet.{" "}
                  <Link to="/standard-items" className="underline">
                    Create one first
                  </Link>
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                min="1"
                value={itemQuantity}
                onChange={(e) => setItemQuantity(e.target.value)}
                placeholder="1"
                className="rounded-sm"
              />
              <p className="text-xs text-slate-500">
                How many units of this standard item are needed for the project?
              </p>
            </div>

            {/* Preview selected item */}
            {selectedStandardItem && (
              <div className="bg-slate-50 rounded-sm p-4 border">
                {(() => {
                  const item = standardItems.find(i => i.item_id === selectedStandardItem);
                  if (!item) return null;
                  return (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Package2 className="h-5 w-5 text-indigo-600" />
                        <span className="font-medium">{item.name}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-slate-500">Parts:</span>{" "}
                          <span className="font-medium">{item.bom_count || 0}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Version:</span>{" "}
                          <span className="font-medium">v{item.bom_version || 1}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Cost:</span>{" "}
                          <span className="font-medium">R{(item.total_cost || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <span className="text-slate-500">Total for {itemQuantity || 1} unit(s):</span>{" "}
                        <span className="font-semibold text-slate-900">
                          R{((item.total_cost || 0) * (parseInt(itemQuantity) || 1)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAddItemDialogOpen(false);
                setSelectedStandardItem("");
                setItemQuantity("1");
              }}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddStandardItem}
              disabled={!selectedStandardItem || addingItem}
              className="rounded-sm"
            >
              {addingItem && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add to Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add One-off Item Dialog */}
      <Dialog open={oneoffDialogOpen} onOpenChange={setOneoffDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add One-off Item</DialogTitle>
            <DialogDescription>
              Create a custom item for this project. Optionally save it as a new standard item for future use.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Item Name *</Label>
                <Input
                  value={oneoffForm.name}
                  onChange={(e) => setOneoffForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Custom Mounting Bracket"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={oneoffForm.quantity}
                  onChange={(e) => setOneoffForm(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 1 }))}
                  className="rounded-sm"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={oneoffForm.description}
                onChange={(e) => setOneoffForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
                className="rounded-sm"
                rows={2}
              />
            </div>

            {/* BOM Builder */}
            <div className="space-y-3">
              <Label>Bill of Materials</Label>
              
              {/* Add Part Row */}
              <div className="flex gap-2">
                <Select value={selectedPart} onValueChange={setSelectedPart}>
                  <SelectTrigger className="flex-1 rounded-sm">
                    <SelectValue placeholder="Select a part" />
                  </SelectTrigger>
                  <SelectContent>
                    {parts.map((part) => (
                      <SelectItem key={part.part_id} value={part.part_id}>
                        {part.name} ({part.sku}) - R{part.unit_cost?.toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Qty"
                  value={partQuantity}
                  onChange={(e) => setPartQuantity(e.target.value)}
                  className="w-20 rounded-sm"
                />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Cost (opt)"
                  value={partCost}
                  onChange={(e) => setPartCost(e.target.value)}
                  className="w-28 rounded-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddPartToOneoff}
                  disabled={!selectedPart}
                  className="rounded-sm"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {/* BOM List */}
              {oneoffForm.bom.length > 0 ? (
                <div className="border rounded-sm">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Part</TableHead>
                        <TableHead className="text-center">Qty</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {oneoffForm.bom.map((line, index) => (
                        <TableRow key={`bom-line-${line.part_sku || line.part_name || index}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{line.part_name}</p>
                              <code className="text-xs text-slate-400">{line.part_sku}</code>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{line.quantity}</TableCell>
                          <TableCell className="text-right">R{line.unit_cost?.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-medium">R{line.line_total?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-slate-400 hover:text-red-600"
                              onClick={() => handleRemovePartFromOneoff(index)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="p-3 bg-slate-50 border-t flex justify-between items-center">
                    <span className="text-sm text-slate-500">{oneoffForm.bom.length} part(s)</span>
                    <span className="font-semibold">
                      Total: R{oneoffForm.bom.reduce((sum, l) => sum + l.line_total, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="border rounded-sm p-6 text-center text-slate-500">
                  <Package2 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No parts added yet</p>
                </div>
              )}
            </div>

            {/* Save as Standard Item */}
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="save-as-standard"
                checked={oneoffForm.save_as_standard}
                onChange={(e) => setOneoffForm(prev => ({ ...prev, save_as_standard: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="save-as-standard" className="font-normal cursor-pointer">
                Save as new Standard Item for future use
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOneoffDialogOpen(false);
                setOneoffForm({ name: "", description: "", bom: [], quantity: 1, save_as_standard: false });
              }}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddOneoffItem}
              disabled={!oneoffForm.name || oneoffForm.bom.length === 0 || addingItem}
              className="rounded-sm"
            >
              {addingItem && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add to Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BOM Detail Modal */}
      <Dialog open={bomDetailOpen} onOpenChange={setBomDetailOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package2 className="h-5 w-5 text-indigo-600" />
              Bill of Materials - {selectedItemForBOM?.standard_item_name || selectedItemForBOM?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedItemForBOM?.item_type === "standard" ? "Standard Item" : "One-off Item"} • 
              Quantity: {selectedItemForBOM?.quantity} • 
              BOM v{selectedItemForBOM?.bom_version_used || 1}
            </DialogDescription>
          </DialogHeader>
          
          {selectedItemForBOM && (() => {
            const supplierGroups = groupBOMBySupplier(selectedItemForBOM);
            const supplierLeadTimes = [];
            
            return (
            <div className="max-h-[60vh] overflow-y-auto">
              {/* Grouped BOM Display */}
              {Object.entries(supplierGroups).map(([supplier, items]) => {
                const supplierCost = 
                  items.parts.reduce((sum, l) => sum + (l.line_total || 0), 0) +
                  items.labour.reduce((sum, l) => sum + (l.line_total || 0), 0);
                const supplierMaxLeadTime = Math.max(
                  ...items.parts.map(l => l.lead_time_days || 0),
                  ...items.labour.map(l => l.lead_time_days || 0),
                  0
                );
                supplierLeadTimes.push(supplierMaxLeadTime);
                
                return (
                <div key={supplier} className="mb-4 border rounded-sm overflow-hidden">
                  <div className="bg-slate-100 px-4 py-2 border-b flex items-center justify-between">
                    <span className="font-medium text-sm">{supplier}</span>
                    {supplierMaxLeadTime > 0 && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {supplierMaxLeadTime} days lead time
                      </span>
                    )}
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-10">Type</TableHead>
                        <TableHead>Component</TableHead>
                        <TableHead className="text-center w-20">Qty/Hrs</TableHead>
                        <TableHead className="text-right w-20">Rate</TableHead>
                        <TableHead className="text-center w-20">Lead Time</TableHead>
                        <TableHead className="text-right w-24">Line Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Parts */}
                      {items.parts.map((line, idx) => (
                        <TableRow key={`part-${idx}`}>
                          <TableCell>
                            <Badge variant="outline" className="rounded-sm text-xs bg-blue-50 text-blue-700 border-blue-200">
                              Part
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{line.part_name}</p>
                              <p className="text-xs text-slate-400">{line.part_sku}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{line.quantity}</TableCell>
                          <TableCell className="text-right text-sm">R{(line.unit_cost || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-center text-sm text-slate-500">
                            {line.lead_time_days ? `${line.lead_time_days}d` : "—"}
                          </TableCell>
                          <TableCell className="text-right font-medium">R{(line.line_total || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Labour */}
                      {items.labour.map((line, idx) => (
                        <TableRow key={`labour-${idx}`}>
                          <TableCell>
                            <Badge variant="outline" className="rounded-sm text-xs bg-purple-50 text-purple-700 border-purple-200">
                              Labour
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{line.labour_name}</p>
                              <p className="text-xs text-slate-400">Hourly labour</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{line.hours} hrs</TableCell>
                          <TableCell className="text-right text-sm">R{(line.hourly_rate || 0).toFixed(2)}/hr</TableCell>
                          <TableCell className="text-center text-sm text-slate-500">
                            {line.lead_time_days ? `${line.lead_time_days}d` : "—"}
                          </TableCell>
                          <TableCell className="text-right font-medium">R{(line.line_total || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Supplier Subtotal */}
                      <TableRow className="bg-slate-50 border-t">
                        <TableCell colSpan={2} className="text-right font-medium text-sm text-slate-600">
                          Subtotal — {supplier}
                        </TableCell>
                        <TableCell className="text-center text-xs text-slate-500">
                          {items.parts.length + items.labour.length} items
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-center text-sm font-medium text-slate-600">
                          {supplierMaxLeadTime > 0 ? `${supplierMaxLeadTime}d` : "—"}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-800">
                          R{supplierCost.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                );
              })}
              
              {/* Cost & Lead Time Summary */}
              <div className="bg-slate-50 rounded-sm px-4 py-3 border mt-4">
                <div className="flex justify-between items-center">
                  <div className="flex gap-6 text-sm">
                    <span className="text-slate-500">
                      {selectedItemForBOM?.bom?.length || 0} parts
                    </span>
                    <span className="text-slate-500">
                      {selectedItemForBOM?.labour_bom?.length || 0} labour
                    </span>
                    {(() => {
                      const totalLeadTime = supplierLeadTimes.length > 0 ? Math.max(...supplierLeadTimes) : 0;
                      return totalLeadTime > 0 ? (
                        <span className="text-slate-600 font-medium flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {totalLeadTime} days total lead time
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-slate-500 mr-2">Unit Cost:</span>
                    <span className="text-lg font-semibold">
                      R{(
                        (selectedItemForBOM?.bom?.reduce((sum, l) => sum + (l.line_total || 0), 0) || 0) +
                        (selectedItemForBOM?.labour_bom?.reduce((sum, l) => sum + (l.line_total || 0), 0) || 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t">
                  <span className="text-sm text-slate-500">× {selectedItemForBOM?.quantity} units</span>
                  <div className="text-right">
                    <span className="text-sm text-slate-500 mr-2">Total:</span>
                    <span className="text-xl font-bold text-indigo-600">
                      R{(selectedItemForBOM?.total_cost || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            );
          })()}
          
          <DialogFooter>
            <Button onClick={() => setBomDetailOpen(false)} className="rounded-sm">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
